import { useContext, useEffect, useState } from "react";
import AuthContext from "../../context/auth/AuthContext";
import {
  calculateEffortRate,
  getUserDisplayName,
  haveValue,
  getCustomIncomeDetails,
  shouldDeductNextView,
  toast,
  convertCurrencyFormat,
  shouldShowCapacityReport,
  resolveIncomeSelection,
} from "../../helper/common";
import useTranslator from "../../hooks/useTranslator";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { calculateNowTime } from "../../services/sharedService";
import LandLordContext from "../../context/landlord/LandLordContext";
import AddPropertyModal from "../../pages/landlord/profile-request/AddPropertyModal";
import NewGuarantorsModal from "../../pages/landlord/profile-request/NewGuarantorsModal";
import ServingPromptModal from "../../pages/landlord/profile-request/ServingPromptModal";
import LoadingSpinner from "../LoadingSpinner";
import { WORKSPACE_ROLES_CAPABILITIES } from "../../Utils/workspaceRoleCapabilities";
import DocsStatus from "./DocsStatus";
import FlatmateModal from "../TenantDetails/FlatmateModal";
import SkorProtectBadge from "../SkorProtectBadge/SkorProtectBadge";

const ProfileRow = ({
  profile,
  properties,
  loadData,
  tenantChecks = false,
}) => {
  const { _t } = useTranslator();
  const location = useLocation();
  const authContext = useContext(AuthContext);
  const { workspaceId } = useParams();
  const { user, checkIeDetails, setLoading } = authContext;
  const landLordContext = useContext(LandLordContext);
  const { editSharedProfile, propertiesAll, deaWorkspace } = landLordContext;
  const navigate = useNavigate();
  const [linkedPropertiesModel, setLinkedPropertiesModel] = useState(false);
  const [defaultPropertyLink, setDefaultPropertyLink] = useState([]);
  const [selectBtn, setSelectBtn] = useState(_t("select") + "...");
  const [propertyLink, setPropertyLink] = useState([]);
  const [propertyLinkedLoading, setPropertyLinkedLoading] = useState(false);
  const [propertyChanged, setPropertyChanged] = useState(false);
  const [guarantorModel, setGuarantorModel] = useState(false);
  const [flatmateModel, setFlatmateModel] = useState(false);
  const [servingPromptModal, setServingPromptModal] = useState(false);

  const tenant = profile?.tenantid;
  const displayName = getUserDisplayName(tenant);
  const nameParts = displayName.split(" ").filter(Boolean);
  const initials = `${(nameParts[0]?.charAt(0) ?? "").toUpperCase()}${(nameParts[1]?.charAt(0) ?? "").toUpperCase()}`;

  useEffect(() => {
    if (haveValue(properties) && haveValue(profile)) {
      if (haveValue(profile.propertyid)) {
        const temp_arr = [];
        var result = profile.propertyid.map((id) => {
          const obj = properties.find((item) => item._id === id);
          if (obj) {
            temp_arr.push(
              `${obj.street} - ${
                Number.isNaN(parseInt(obj.street_number, 10))
                  ? "N/A"
                  : String(parseInt(obj.street_number, 10))
              }, ${obj.floor_number}-${obj.flat_number}`
            );
            return {
              value: obj?._id,
              label: `${obj.street} - ${
                Number.isNaN(parseInt(obj.street_number, 10))
                  ? "N/A"
                  : String(parseInt(obj.street_number, 10))
              }, ${obj.floor_number}-${obj.flat_number}`,
            };
          }
          return null; // Handle case where id is not found in array1
        });

        result = result.filter((val) => haveValue(val));

        var placeholder_string = [];

        temp_arr.forEach((val) => {
          placeholder_string.push(
            <span className="placeholder_string">{val}</span>
          );
        });
        if (placeholder_string.length == 0) setSelectBtn(_t("select") + "...");
        else setSelectBtn(placeholder_string);

        setPropertyLink(profile?.propertyid);
        setDefaultPropertyLink(result);
      }
    }
  }, [properties, profile]);

  useEffect(() => {
    const isEqual =
      JSON.stringify([...(propertyLink ?? [])].map((i) => i).sort()) ===
      JSON.stringify(
        [...(defaultPropertyLink ?? [])].map((i) => i.value).sort()
      );

    setPropertyChanged(!isEqual);
  }, [propertyLink, defaultPropertyLink, linkedPropertiesModel]);

  const handleLinkProperty = async (
    propertyToAdd = null,
    propertyToRemove = null
  ) => {
    if (haveValue(propertyToAdd)) {
      const data = {
        property_id: propertyToAdd,
        status: "add",
      };
      setPropertyLinkedLoading(true);
      await editSharedProfile(profile?._id, data)
        .then(async (res) => {
          await loadData();
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          setPropertyLinkedLoading(false);
        });
    } else if (haveValue(propertyToRemove)) {
      const data = {
        property_id: propertyToRemove,
        status: "remove",
      };
      setPropertyLinkedLoading(true);
      await editSharedProfile(profile?._id, data)
        .then(async (res) => {
          await loadData();
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          setPropertyLinkedLoading(false);
        });
    } else {
      toast.error(_t("pleaseSelectPropertyToLink"));
    }
  };

  const handleUnlinkProperty = async (propertyId) => {
    setPropertyLink(propertyLink.filter((val) => val !== propertyId));
    await handleLinkProperty(null, propertyId);
  };

  const eachRentAmount = (fullinfo) => {
    const user_income_sources = resolveIncomeSelection(
      fullinfo?.income_selection,
      fullinfo?.tenantid?._id
    );
    const fallbackIncome = fullinfo?.useFallbackIncome;
    const useFallbackIncome =
      typeof fallbackIncome === "boolean" ? fallbackIncome : true;
    const gurantor_income_sources = user_income_sources?.guarantor_sources;
    const disabledIncomeSourcesIds = fullinfo?.excluded_income_source_ids ?? [];
    let profile_effort_rate_percentage =
      fullinfo?.effort_rate_data?.tenant?.effort_rate_percentage;
    if (!haveValue(profile_effort_rate_percentage)) {
      profile_effort_rate_percentage =
        user?.current_workspace?.default_effort_rate ?? 35;
    }
    const effortRateType =
      fullinfo?.effort_rate_data?.tenant?.effort_rate_type ??
      fullinfo?.tenantid?.effort_rate_type ??
      "tenant";

    const tInfo = fullinfo?.tenantid;
    const user_income = getCustomIncomeDetails(
      tInfo,
      user,
      _t,
      user_income_sources?.tenant_source,
      undefined,
      useFallbackIncome,
      deaWorkspace,
      fullinfo?.custom_income_sources
    );

    // gurantor's income
    let totalGurantorIncome = 0;
    let allGurantorIncomes = [];
    let allGurantors = fullinfo?.tenantid?.gurantor_id;

    if (Array.isArray(allGurantors)) {
      allGurantors.forEach((gurantor) => {
        if (haveValue(gurantor)) {
          const income = getCustomIncomeDetails(
            gurantor,
            user,
            _t,
            gurantor_income_sources?.[gurantor?._id],
            undefined,
            useFallbackIncome,
            deaWorkspace,
            fullinfo?.custom_income_sources
          );

          // if (!disabledIncomeSourcesIds?.includes(income?.all_sources?._id)) {
          if (["guarantor", "both"]?.includes(effortRateType)) {
            totalGurantorIncome += income?.amount || 0;
          }
          // }
          allGurantorIncomes.push(income);
        }
      });
    }

    const userAmount = ["tenant", "both"]?.includes(effortRateType)
      ? +user_income?.amount
      : 0;

    const combinedIncome = userAmount + +totalGurantorIncome;

    const data = {
      ...fullinfo,
      rent_percentage: profile_effort_rate_percentage,
    };
    let temp_obj = calculateEffortRate(combinedIncome, user, data);

    return {
      combinedIncome,
      effort_rate: !Number.isNaN(temp_obj?.effort_amount)
        ? Math.round(Number(temp_obj.effort_amount))
        : 0,
      profile_effort_rate_percentage,
    };
  };

  const handleOpenProfile = async () => {
    try {
      if (
        tenantChecks &&
        profile?.tenantid?.profile_completed == false &&
        WORKSPACE_ROLES_CAPABILITIES?.tenantChecks?.edit?.includes(
          user?.current_workspace_role
        )
      ) {
        navigate(
          `/${workspaceId}/tenant-check/create?tenant_id=${profile?.tenantid?._id}`
        );
        return;
      }
      setLoading(true);
      const checkView = await shouldDeductNextView(user, profile?._id);
      if (checkView) {
        setServingPromptModal(true);
      } else {
        // user?.isParentLL
        //   ? navigate(
        //       `/${workspaceId}/profile-property?agencylink=${profile?._id}${
        //         tenantChecks ? "&profile_type=tenant_check" : ""
        //       }`
        //     )
        //   :
        navigate(
          `/${workspaceId}/profile-property?sharedprofileid=${profile?._id}${
            tenantChecks ? "&profile_type=tenant_check" : ""
          }`,
          {
            state: { from: location.pathname + location.search },
          }
        );
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const isHighlight =
    profile?.opened_at &&
    profile?.sortDate &&
    new Date(profile.sortDate) > new Date(profile.opened_at);

  const isProfileCompleted =
    !profile?.tenantid?.tenant_extra_field_id?.landlord_ids_for_forensic_notification?.some(
      (i) => i?.profile_id === profile?._id
    );

  const missingCount = profile?.tenantid?.missingCount || 0;

  const calculateScore = (userDetails) => {
    var result = 0;

    if (checkIeDetails(userDetails)) {
      var totalScore =
        (userDetails?.paymentScores?.creditScore?.value || 0) / 2;

      if (totalScore > 850) {
        totalScore = 850;
      }
      result = totalScore;
    } else {
      if (haveValue(userDetails?.paymentScores?.creditScore?.value)) {
        result = userDetails?.paymentScores?.creditScore?.value;
      }
    }

    return result;
  };

  return (
    <>
      <tr
        className={`hideOnMobile second-row relative ${
          isHighlight ? "highlightRow" : ""
        }`}
      >
        <td
          onClick={() => {
            if (isProfileCompleted) handleOpenProfile();
          }}
          className={`${isProfileCompleted ? "cursor-pointer" : ""}`}
        >
          <div
            className="d-flex align-items-center custom-gap-3"
            style={{ marginInlineEnd: "0.5vw" }}
          >
            <div
              className="earner-thumbnail-wrapper d-flex align-items-center justify-content-center"
              style={{ backgroundColor: "#A3EB64" }}
            >
              <span
                style={{ color: "#253837", fontWeight: 600, fontSize: "10px" }}
              >
                {initials || "U"}
              </span>
            </div>
            {getUserDisplayName(profile?.tenantid)}
            {profile?.tenantid?.skor_protect && <SkorProtectBadge />}
          </div>
        </td>

        <td className={"skor-details-column"}>
          {calculateScore(profile?.tenantid)}
        </td>

        <td
          onClick={() => {
            if (isProfileCompleted) handleOpenProfile();
          }}
          className={`${isProfileCompleted ? "cursor-pointer" : ""}`}
        >
          €{convertCurrencyFormat(eachRentAmount(profile)?.combinedIncome)}
        </td>

        <td
          onClick={() => {
            if (isProfileCompleted) handleOpenProfile();
          }}
          className={`${isProfileCompleted ? "cursor-pointer" : ""}`}
        >
          €{convertCurrencyFormat(eachRentAmount(profile)?.effort_rate)}
        </td>

        {/* <td>
          <div className="d-flex align-items-center">
            <div
              onClick={() => {
                setFlatmateModel(true);
              }}
              className={`d-flex align-items-center bg-[#DBF1CA] cursor-pointer bg_test removeGreenBg`}
            >
              <div className="d-flex align-items-center custom-gap-2">
                <p className="linked_p">
                  {profile?.tenantid?.sharers?.length ?? 0}
                </p>
                <img className="" src="/images/arrow-right-green.svg" />
              </div>
            </div>
          </div>
        </td> */}

        <td>
          <div className="d-flex align-items-center">
            <div
              onClick={() => {
                if (
                  WORKSPACE_ROLES_CAPABILITIES?.tenants?.viewGuarantors?.includes(
                    user?.current_workspace_role
                  )
                ) {
                  setGuarantorModel(true);
                }
              }}
              className={`d-flex align-items-center bg-[#DBF1CA] cursor-pointer bg_test removeGreenBg`}
            >
              <div className="d-flex align-items-center custom-gap-2">
                <p className="linked_p">
                  {(profile?.tenantid?.gurantor_id ?? []).length}
                </p>
                {WORKSPACE_ROLES_CAPABILITIES?.tenants?.viewGuarantors?.includes(
                  user?.current_workspace_role
                ) && <img className="" src="/images/arrow-right-green.svg" />}
              </div>
            </div>
          </div>
        </td>

        <td>
          <div className="d-flex align-items-center">
            <div
              onClick={() => {
                setLinkedPropertiesModel(true);
              }}
              className={`d-flex align-items-center bg-[#DBF1CA] cursor-pointer bg_test removeGreenBg ${
                profile?.propertyid?.length === 0
                  ? "btngreen"
                  : "link_property_bg"
              }`}
            >
              <div className="d-flex align-items-center custom-gap-2">
                <p className="linked_p">
                  {profile?.propertyid?.filter((o) =>
                    new Set(propertiesAll?.map((x) => x._id?.toString()))?.has(
                      o?.toString()
                    )
                  ).length || 0}
                </p>
                <img className="" src="/images/arrow-right-green.svg" />
                {WORKSPACE_ROLES_CAPABILITIES?.properties?.linkTenant?.includes(
                  user?.current_workspace_role
                ) && (
                  <div className="cursor-pointer editButton">
                    <img src="/images/addGreen.svg" />
                    <p>{_t("add")}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </td>

        <td>
          <DocsStatus missingCount={missingCount} />
        </td>

        <td
          onClick={() => {
            if (isProfileCompleted) handleOpenProfile();
          }}
          className={`${isProfileCompleted ? "cursor-pointer" : ""}`}
        >
          <div className="d-flex custom-gap-2 align-items-center">
            {!isProfileCompleted ? (
              <div
                style={{
                  color: "#e4eee8",
                  backgroundColor:
                    profile?.tenantid?.step <= 3 ||
                    profile?.tenantid?.step === 4 ||
                    (profile?.tenantid?.step <= 9 &&
                      profile?.tenantid?.profile_completed === false)
                      ? "#FFFBEC"
                      : "",
                  border:
                    profile?.tenantid?.step <= 3 ||
                    profile?.tenantid?.step === 4 ||
                    (profile?.tenantid?.step <= 9 &&
                      profile?.tenantid?.profile_completed === false)
                      ? "1px solid #F2E09B"
                      : "",
                }}
                className="d-flex processing-docs-card justify-content-between align-items-center"
              >
                {profile?.tenantid?.step <= 3 ? (
                  <div
                    className="d-flex justify-content-between align-items-center documents-container-info-card-img"
                    style={{ color: "#E1BB20", backgroundColor: "#FFFBEC" }}
                  >
                    <span>{_t("pendingDocUpload")}</span>
                  </div>
                ) : profile?.tenantid?.step === 4 ? (
                  <div
                    className="d-flex justify-content-between align-items-center documents-container-info-card-img"
                    style={{ color: "#E1BB20", backgroundColor: "#FFFBEC" }}
                  >
                    <span>{_t("pendingKyc")}</span>
                  </div>
                ) : profile?.tenantid?.step <= 9 &&
                  profile?.tenantid?.profile_completed === false ? (
                  <div
                    className="d-flex justify-content-between align-items-center documents-container-info-card-img"
                    style={{ color: "#E1BB20", backgroundColor: "#FFFBEC" }}
                  >
                    <span>{_t("pendingConsent")}</span>
                  </div>
                ) : (
                  <div className="d-flex justify-content-between align-items-center documents-container-info-card-img">
                    <LoadingSpinner
                      size={"0.6944vw"}
                      color="#253837"
                      borderWidth={1}
                      className={"docs-loading-spinner"}
                    />
                    <span>{_t("processingProfile")}</span>
                  </div>
                )}
              </div>
            ) : (
              <div
                className={`${
                  isHighlight ? "d-flex align-items-center custom-gap-2" : ""
                }`}
              >
                {calculateNowTime(
                  profile?.sortDate,
                  localStorage.getItem("i18nextLng")
                )}
              </div>
            )}
            {isHighlight && <p className="recentText">{_t("recent")}</p>}
          </div>
        </td>

        {isProfileCompleted ? (
          <td
            onClick={() => {
              handleOpenProfile();
            }}
          >
            <div className="cursor-pointer">
              <img
                className="arrowRightTable"
                src="/images/arrow-right-green.svg"
              />
            </div>
          </td>
        ) : (
          <td></td>
        )}
      </tr>
      {/* Mobile Card Structure */}
      <div
        className={`table-mobile-card hideOnDesktop 
        ${isHighlight ? "highlightMobileCard" : ""}`}
      >
        {/* Tenant */}
        <div
          className="d-flex justify-content-between gap-3 align-items-center mb-3 cursor-pointer"
          onClick={() => {
            if (isProfileCompleted) handleOpenProfile();
          }}
        >
          <div className="d-flex justify-content-start gap-3 align-items-center">
            <div
              className="earner-thumbnail-wrapper d-flex items-center justify-center mr-3"
              style={{ backgroundColor: "#A3EB64" }}
            >
              <span
                style={{ color: "#253837", fontWeight: 600, fontSize: "14px" }}
              >
                {initials || "U"}
              </span>
            </div>
            <div className="main-text d-flex flex-column gap-1">
              {getUserDisplayName(profile?.tenantid)}
              {profile?.tenantid?.skor_protect && <SkorProtectBadge />}
            </div>
          </div>
          <div className="d-flex justify-content-start gap-4 align-items-center">
            {isHighlight && <p className="recentText ml-2">{_t("recent")}</p>}
            <div
              className="flex justify-end cursor-pointer"
              onClick={() => {
                if (isProfileCompleted) handleOpenProfile();
              }}
            >
              <img
                className="profile-row-arrow-btn"
                src="/images/arrow-right-green.svg"
              />
            </div>
          </div>
        </div>

        {/* Score */}
        <div className="table-mobile-card-comp">
          {" "}
          <div
            className="table-mobile-card-data cursor-pointer"
            onClick={() => {
              if (isProfileCompleted) handleOpenProfile();
            }}
          >
            <p className="text-gray-500">{_t("income")}</p>
            <span className="card-text">
              €{convertCurrencyFormat(eachRentAmount(profile)?.combinedIncome)}
            </span>
          </div>
          {/* Effort Rate */}
          <div
            className="table-mobile-card-data cursor-pointer"
            onClick={() => {
              if (isProfileCompleted) handleOpenProfile();
            }}
          >
            <p className="text-gray-500">{_t("effortRate")}</p>
            <span className="card-text">
              €{convertCurrencyFormat(eachRentAmount(profile)?.effort_rate)}
            </span>
          </div>
          {/* Flatmates */}
          <div className="table-mobile-card-data">
            <p className="text-gray-500">{_t("flatmates")}</p>
            <div
              onClick={() => setFlatmateModel(true)}
              className="d-flex align-items-center cursor-pointer"
            >
              <p className="card-text">
                {(profile?.tenantid?.sharers?.length ?? 0) +
                  (profile?.tenantid?.tenant_extra_field_id?.selected_onboarding_users?.filter(
                    (item) => item?.type === "sharer"
                  )?.length ?? 0)}
              </p>
              <img src="/images/arrow-right-green.svg" className="px-2" />
            </div>
          </div>
          {/* Guarantors */}
          <div className="table-mobile-card-data">
            <p className="text-gray-500">{_t("guarantors")}</p>
            <div
              onClick={() => setGuarantorModel(true)}
              className="d-flex align-items-center cursor-pointer"
            >
              <p className="card-text">
                {(profile?.tenantid?.gurantor_id ?? []).length}
              </p>
              <img src="/images/arrow-right-green.svg" className="px-2" />
            </div>
          </div>
          {/* Linked Properties */}
          <div className="table-mobile-card-data">
            <p className="text-gray-500">{_t("linkedProperties")}</p>
            <div
              onClick={() => setLinkedPropertiesModel(true)}
              className={`d-flex align-items-center cursor-pointer`}
            >
              <p className="card-text">{profile?.propertyid?.length || 0}</p>
              <img src="/images/arrow-right-green.svg" className="px-2" />
              <div className="d-flex align-items-center card-add-btn cursor-pointer">
                <img src="/images/addGreen.svg" />
                <p className="">{_t("add")}</p>
              </div>
            </div>
          </div>
          {/* Docs */}
          <div className="table-mobile-card-data">
            <p className="text-gray-500">{_t("docs")}</p>
            <DocsStatus missingCount={missingCount} />
          </div>
          {/* Assign Agent / Landlord */}
          {/* {user?.isParentLL && (
            <div className="table-mobile-card-data">
              <p className="text-gray-500">{_t("assignAgent/Landlord")}</p>
              <div
                className="d-flex align-items-center cursor-pointer"
                onClick={() => openAssignLLModal(profile?.tenantid?._id)}
              >
                <span className="card-text">{profile.agent_count}</span>
                <img src="/images/arrow-right-green.svg" className="px-2" />

                <div className="d-flex align-items-center card-add-btn cursor-pointer">
                  <img src="/images/addGreen.svg" />
                  <p className="">{_t("add")}</p>
                </div>
              </div>
            </div>
          )} */}
          {/* Info Updated At */}
          <div
            className="table-mobile-card-data cursor-pointer"
            onClick={() => {
              if (isProfileCompleted) handleOpenProfile();
            }}
          >
            <p className="text-gray-500">{_t("infoUpdatedAt")}</p>
            {!isProfileCompleted ? (
              <div
                style={{
                  color: "#e4eee8",
                  backgroundColor:
                    profile?.tenantid?.step <= 3 ||
                    profile?.tenantid?.step === 4 ||
                    (profile?.tenantid?.step <= 9 &&
                      profile?.tenantid?.profile_completed === false)
                      ? "#FFFBEC"
                      : "",
                  border:
                    profile?.tenantid?.step <= 3 ||
                    profile?.tenantid?.step === 4 ||
                    (profile?.tenantid?.step <= 9 &&
                      profile?.tenantid?.profile_completed === false)
                      ? "1px solid #F2E09B"
                      : "",
                }}
                className="d-flex processing-docs-card justify-content-between align-items-center"
              >
                {profile?.tenantid?.step <= 3 ? (
                  <div
                    className="d-flex justify-content-between align-items-center documents-container-info-card-img"
                    style={{ color: "#E1BB20", backgroundColor: "#FFFBEC" }}
                  >
                    <span>{_t("pendingDocUpload")}</span>
                  </div>
                ) : profile?.tenantid?.step === 4 ? (
                  <div
                    className="d-flex justify-content-between align-items-center documents-container-info-card-img"
                    style={{ color: "#E1BB20", backgroundColor: "#FFFBEC" }}
                  >
                    <span>{_t("pendingKyc")}</span>
                  </div>
                ) : profile?.tenantid?.step <= 9 &&
                  profile?.tenantid?.profile_completed === false ? (
                  <div
                    className="d-flex justify-content-between align-items-center documents-container-info-card-img"
                    style={{ color: "#E1BB20", backgroundColor: "#FFFBEC" }}
                  >
                    <span>{_t("pendingConsent")}</span>
                  </div>
                ) : (
                  <div className="d-flex justify-content-between align-items-center documents-container-info-card-img">
                    <LoadingSpinner
                      size={"0.5556vw"}
                      color="#253837"
                      borderWidth={1}
                      className={"docs-loading-spinner"}
                    />
                    <span>{_t("processingProfile")}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="d-flex align-items-center card-text">
                {calculateNowTime(
                  profile?.sortDate,
                  localStorage.getItem("i18nextLng")
                )}
              </div>
            )}
          </div>
        </div>

        {/* Action (Arrow) */}
      </div>

      <AddPropertyModal
        isOpen={linkedPropertiesModel}
        onClose={() => setLinkedPropertiesModel(false)}
        properties={properties}
        profile={profile}
        handleUnlinkProperty={(id) => handleUnlinkProperty(id)}
        handleLinkProperty={handleLinkProperty}
        setPropertyLink={setPropertyLink}
      />
      <FlatmateModal
        user={profile?.tenantid}
        sharerData={
          profile?.sharersData ??
          profile?.tenantid?.sharers?.map((s) => ({
            ...s,
          })) ??
          []
        }
        isOpen={flatmateModel}
        onClose={() => setFlatmateModel(false)}
        llShowOnly={true}
        usedFor={"tenantListing"}
      />
      <NewGuarantorsModal
        isOpen={guarantorModel}
        user={user}
        profileId={profile?._id}
        guarantorsList={profile?.tenantid?.gurantor_id ?? []}
        onClose={() => setGuarantorModel(false)}
      />
      <ServingPromptModal
        openModal={servingPromptModal}
        profile={profile}
        URL={
          // user?.isParentLL
          //   ? `/${workspaceId}/profile-property?agencylink=${profile?._id}`
          //   :
          `/${workspaceId}/profile-property?sharedprofileid=${profile?._id}`
        }
        setOpenModal={setServingPromptModal}
      />
    </>
  );
};

export default ProfileRow;
